import { useState } from "react";
import { usePostHog } from "@posthog/react";
import {
  captureAdultGigPublishOutcome,
  type AdultGigCreateFlowAnalyticsContext,
  type AdultGigCreateFlowEntryPoint,
  type AdultGigCreateFlowSource,
} from "../../../lib/analytics";
import { gigsApi } from "../../../lib/gigs";
import type { CreateGigRequest, CreateGigResponse, GigDetailResponse } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { inferPosterPaymentAmountCentsFromTeenPayout } from "../../../utils/GigPricingUtils";
import type { CreateGigFormValues } from "../../gigs/creategig";

export type AdultGigEditorMode = "draft" | "published" | "repost";
export type AdultGigEditRestrictions = {
  hasActiveRequests: boolean;
};

type AdultGigEditorSuccessArgs = {
  gigId: string;
  mode: AdultGigEditorMode;
  payload: CreateGigRequest;
  response: CreateGigResponse;
};

type UseAdultGigEditorOptions = {
  analyticsSource?: AdultGigCreateFlowSource;
  analyticsEntryPointByMode?: Partial<Record<AdultGigEditorMode, AdultGigCreateFlowEntryPoint>>;
  onError?: (message: string) => void;
  onSuccess?: (args: AdultGigEditorSuccessArgs) => Promise<void> | void;
};

export const toCreateGigFormValues = (
  gig: GigDetailResponse,
  resetAvailabilityWindow: boolean,
): CreateGigFormValues => ({
  title: gig.title,
  description: gig.description,
  requiresEquipment: gig.requiresEquipment,
  equipmentDescription: gig.equipmentDescription,
  category: gig.category,
  locationType: gig.locationType,
  city: gig.city ?? "",
  state: gig.state ?? "",
  zip: gig.zip ?? "",
  timeZone: gig.timeZone ?? null,
  startTime: resetAvailabilityWindow ? null : gig.startTime,
  endTime: resetAvailabilityWindow ? null : gig.endTime,
  scheduleType: gig.scheduleType,
  scheduleNotes: gig.scheduleNotes,
  preferredStartTime: gig.preferredStartTime,
  preferredEndTime: gig.preferredEndTime,
  posterPaymentAmountCents: gig.posterPaymentAmountCents
    ?? (typeof gig.paymentAmountCents === "number"
      ? inferPosterPaymentAmountCentsFromTeenPayout(gig.paymentAmountCents)
      : null)
    ?? gig.paymentAmountCents,
  volunteer: gig.volunteer,
  communityServiceHoursEligible: gig.communityServiceHoursEligible,
  communityServiceProofDetails: gig.communityServiceProofDetails,
  estimatedDurationMinutes: gig.estimatedDurationMinutes,
  teensRequired: gig.teensRequired ?? 1,
});

const useAdultGigEditor = ({
  analyticsSource = "adult_posted_gigs",
  analyticsEntryPointByMode = {},
  onError,
  onSuccess,
}: UseAdultGigEditorOptions = {}) => {
  const posthog = usePostHog();
  const [editGigId, setEditGigId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<AdultGigEditorMode | null>(null);
  const [editInitialValues, setEditInitialValues] = useState<CreateGigFormValues | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editRestrictions, setEditRestrictions] = useState<AdultGigEditRestrictions | null>(null);

  const closeEditor = () => {
    setEditGigId(null);
    setEditMode(null);
    setEditInitialValues(null);
    setIsEditSubmitting(false);
    setEditRestrictions(null);
  };

  const openEditor = async (gigId: string, mode: AdultGigEditorMode, restrictions: AdultGigEditRestrictions | null = null) => {
    try {
      const gigDetail = await gigsApi.getById(gigId);
      setEditGigId(gigId);
      setEditMode(mode);
      setEditInitialValues(toCreateGigFormValues(gigDetail, mode === "repost"));
      setEditRestrictions(restrictions);
    } catch (requestError) {
      onError?.(apiErrorToMessage(requestError, "Failed to load gig details."));
    }
  };

  const submitEditor = async (
    payload: CreateGigRequest,
    images: File[] = [],
    analyticsContext?: AdultGigCreateFlowAnalyticsContext,
  ) => {
    if (!editGigId || !editMode) {
      throw new Error("No gig selected.");
    }

    setIsEditSubmitting(true);

    try {
      const response =
        editMode === "repost"
          ? await gigsApi.repostGig(editGigId, payload)
          : await gigsApi.updateEditableGig(editGigId, payload);

      if (editMode !== "published") {
        captureAdultGigPublishOutcome(posthog, {
          gig_id: response.id,
          status: payload.status === "DRAFT" ? "DRAFT" : "PUBLISHED",
          source: analyticsSource,
          ...(analyticsContext?.flow_id ? { flow_id: analyticsContext.flow_id } : {}),
          ...(analyticsContext?.entry_point ? { entry_point: analyticsContext.entry_point } : {}),
          ...(analyticsContext?.mode ? { mode: analyticsContext.mode } : {}),
          category: payload.category,
          volunteer: Boolean(payload.volunteer),
          schedule_type: payload.scheduleType ?? "FIXED",
          has_images: images.length > 0,
          image_count: images.length,
          teens_required: payload.teensRequired ?? 1,
          poster_payment_amount_cents: payload.paymentAmountCents,
        });
      }

      await onSuccess?.({
        gigId: editGigId,
        mode: editMode,
        payload,
        response,
      });
      closeEditor();
    } catch (requestError) {
      const fallbackMessage = editMode === "repost" ? "Failed to repost gig." : "Failed to update gig.";
      const message = apiErrorToMessage(requestError, fallbackMessage);
      onError?.(message);
      throw new Error(message);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  return {
    openEditor,
    closeEditor,
    modalProps: {
      isOpen: editGigId !== null,
      isSubmitting: isEditSubmitting,
      onClose: closeEditor,
      onSubmit: submitEditor,
      modalTitle: editMode === "repost" ? "Edit and Repost Gig" : editMode === "published" ? "Edit Gig" : "Continue Draft",
      publishLabel: editMode === "repost" ? "Save and Repost" : editMode === "published" ? "Save Changes" : "Publish Gig",
      publishingLabel: editMode === "repost" ? "Republishing..." : editMode === "published" ? "Saving..." : "Publishing...",
      showSaveDraft: editMode === "draft",
      draftLabel: "Save draft",
      draftingLabel: "Saving draft...",
      initialValues: editInitialValues,
      restrictPublishedFields: editRestrictions?.hasActiveRequests ?? false,
      analyticsSource,
      analyticsEntryPoint: editMode ? analyticsEntryPointByMode[editMode] : undefined,
      analyticsMode: editMode ?? undefined,
    },
  };
};

export default useAdultGigEditor;
